import { useCallback, isValidElement, cloneElement, memo } from 'react';
import jsonExport from 'jsonexport/dist';
import type { WorkSheet } from 'xlsx';
import _get from 'lodash.get';
import { Button, ButtonProps } from '@mui/material';
import { useNotify, useLoader } from '../../hooks';
import {
  authGet,
  formatDate,
  formatPhone,
  formatCurrency,
  isObject,
  formatNumber,
} from '../../lib';
import { MouseChangeEventType, PagedResults } from '../../types';
// import { GetAppIcon } from '../../assets';

export interface HeaderTypes<TRow = Record<string, any>> {
  key: string;
  label: string;
  formatProperty?: string;
  formatFunc?: (row: TRow) => string | null | undefined;
  nestedKey?: string;
  customKey?: string;
  format?: 'date' | 'phone' | 'currency' | 'number';
}

interface Props<TRow = Record<string, any>> {
  path: string;
  queryParams: object;
  headersArray: Array<HeaderTypes<TRow>>;
  fileName: string;
  btnProps?: ButtonProps;
  customBtn?: React.ReactElement<any>;
  dataKey?: string;
  transformData?: (data: Array<TRow>) => Array<TRow>;
  exportFormat?: 'csv' | 'xlsx';
  label?: string;
  links?: Array<ExportLink<TRow>>;
  sheetName?: string;
}

interface ExportLink<TRow> {
  key: string;
  getUrl: (row: TRow) => string | undefined;
}

interface ClonedButton {
  onClick: (e: MouseChangeEventType) => Promise<void>;
}

const ExportButtonComponent = <TRow = Record<string, any>,>({
  path,
  queryParams,
  headersArray,
  fileName,
  btnProps = {},
  customBtn,
  dataKey,
  transformData,
  exportFormat = 'csv',
  label = 'Export',
  links,
  sheetName,
}: Props<TRow>) => {
  const notify = useNotify();
  const setLoading = useLoader();

  const fetchData = useCallback(
    async (e: MouseChangeEventType) => {
      setLoading(true);
      const response = await authGet<PagedResults<TRow>>([path, queryParams]);
      setLoading(false);
      const { data: _data, error } = response;
      if (error) {
        return notify(
          error.message || error.Message || 'Error exporting data',
          'error',
        );
      }
      const data = dataKey ? _get(_data, dataKey, []) : _data;
      const dataArray: Array<TRow> = Array.isArray(data) ? data : data.results;
      const exportData = transformData ? transformData(dataArray) : dataArray;
      const { rename, headers, fields } = generateFieldsForExport<TRow>(
        exportData,
        headersArray,
      );
      if (exportFormat === 'xlsx') {
        await downloadXlsx(
          fields,
          headers || [],
          rename || [],
          exportData,
          links,
          fileName,
          sheetName,
        );
        return;
      }

      jsonExport(fields, { headers, rename }, (_err, csv) => downloadCSV(csv, fileName));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataKey, exportFormat, fileName, headersArray, links, notify, path, queryParams, sheetName, transformData],
  );

  // TODO disable if no list items
  return isValidElement(customBtn) ? (
    cloneElement(customBtn as React.ReactElement<ClonedButton>, {
      onClick: fetchData,
    })
  ) : (
    <Button
      // className={classes.btn}
      color='primary'
      size='large'
      variant='contained'
      // startIcon={<GetAppIcon />}
      onClick={fetchData}
      {...btnProps}
    >
      {label}
    </Button>
  );
};

export const ExportButton = memo(ExportButtonComponent) as <TRow>(
  props: Props<TRow>,
) => React.ReactElement<any>;

// code from https://github.com/marmelab/react-admin/blob/master/packages/ra-core/src/export/downloadCSV.ts
export function downloadCSV(csv, filename) {
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${filename}.csv`);
}

function downloadBlob(blob: Blob, filename: string) {
  const fakeLink = document.createElement('a');
  fakeLink.style.display = 'none';
  document.body.appendChild(fakeLink);
  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
    // Manage IE11+ & Edge
    (window.navigator as any).msSaveOrOpenBlob(blob, filename);
  } else {
    const url = URL.createObjectURL(blob);
    fakeLink.setAttribute('href', url);
    fakeLink.setAttribute('download', filename);
    fakeLink.click();
    URL.revokeObjectURL(url);
  }
  fakeLink.remove();
}

async function downloadXlsx<TRow>(
  fields: Array<TRow>,
  headers: Array<string>,
  rename: Array<string>,
  exportData: Array<TRow>,
  links: Array<ExportLink<TRow>> | undefined,
  filename: string,
  sheetName?: string,
) {
  const XLSX = await import('xlsx');
  const worksheet = XLSX.utils.json_to_sheet(fields, { header: headers });
  XLSX.utils.sheet_add_aoa(worksheet, [rename], { origin: 'A1' });
  worksheet['!cols'] = headers.map((header, index) => ({
    wch: Math.min(Math.max(rename[index].length, ...fields.map((row) => String(row[header] ?? '').length)) + 2, 36),
  }));
  worksheet['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: fields.length, c: headers.length - 1 } }) };

  links?.forEach((link) => addWorksheetLinks(
    worksheet,
    headers.indexOf(link.key),
    exportData,
    link,
    XLSX.utils.encode_cell,
  ));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, getWorksheetName(sheetName || filename));
  const file = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', compression: true });
  downloadBlob(
    new Blob([file], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${filename}.xlsx`,
  );
}

function addWorksheetLinks<TRow>(
  worksheet: WorkSheet,
  column: number,
  data: Array<TRow>,
  link: ExportLink<TRow>,
  encodeCell: (cell: { r: number; c: number }) => string,
) {
  if (column < 0) return;

  data.forEach((row, index) => {
    const url = link.getUrl(row);
    const cell = worksheet[encodeCell({ r: index + 1, c: column })];
    if (url && cell?.v) {
      cell.l = { Target: url, Tooltip: cell.v };
    }
  });
}

function getWorksheetName(name: string) {
  return name.replace(/[\\/*?:[\]]/g, '').slice(0, 31) || 'Export';
}

export function generateFieldsForExport<TRow = Record<string, any>>(
  data: Array<TRow>,
  headersArray: Array<HeaderTypes<TRow>>,
) {
  if (!headersArray) return { fields: data };
  const rename: Array<string> = [];
  const headers: Array<string> = [];
  const fields: Array<TRow> = [];
  // get headers and rename fields
  headersArray.forEach((h) => {
    rename.push(h.label);
    // if it's an object use the nested key as the key
    headers.push(h.customKey || h.nestedKey || h.key);
  });
  // get data values thats in the headersArray
  data.forEach((row) => {
    const valuesObj = headersArray.reduce<TRow>((acc, cur) => {
      let key = cur.key;
      if (Object.prototype.hasOwnProperty.call(row, key)) {
        let value = row[key];
        if (Array.isArray(value)) {
          if (cur.formatProperty) {
            value = value.map((v) => v[cur.formatProperty!]).join(',');
          } else {
            value = value.join(',');
          }
        } else if (isObject(value)) {
          value = _get(value, cur.nestedKey as string);
          key = cur.nestedKey || '';
        }
        if (cur.format) {
          value = formatters[cur.format]?.(value) || value;
        }
        if (typeof cur.formatFunc === 'function') {
          value = cur.formatFunc(row);
        }
        acc[cur.customKey || key] = value;
      }
      return acc;
    }, {} as TRow);
    fields.push(valuesObj);
  });
  return { rename, headers, fields };
}

const formatters = {
  date: formatDate,
  phone: formatPhone,
  currency: formatCurrency,
  number: formatNumber,
};
